import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useEffect, useState } from 'react';
import StyledFileInput from './StyledFileInput';
import { createItem } from 'services/items/create-item';
import { getItems } from 'services/items/get-items';
import { deleteItem } from 'services/items/delete-item';
import { editItem } from 'services/items/edit-item';

export default function Items() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [openAdd, setOpenAdd] = useState(false);
  const [name, setName] = useState('');
  const [gems, setGems] = useState('');
  const [coins, setCoins] = useState('');
  const [rarity, setRarity] = useState('');
  const [type, setType] = useState('');
  const [image, setImage] = useState('');
  const [unrealItemId, setUnrealItemId] = useState('');
  const [tags, setTags] = useState('');
  const [metadata, setMetadata] = useState('');
  const [itemId, setItemId] = useState('');
  const [isActive, setIsActive] = useState(true);
  
  // Filter states
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterRarity, setFilterRarity] = useState('');

  const selectValues = [
    {
      value: 'COMMON',
      name: 'Common',
      color: 'grey'
    },
    {
      value: 'RARE',
      name: 'Rare',
      color: 'blue'
    },
    {
      value: 'EPIC',
      name: 'Epic',
      color: 'purple'
    },
    {
      value: 'LEGENDARY',
      name: 'Legendary',
      color: 'gold'
    },
    {
      value: 'SUPREME',
      name: 'Supreme',
      color: 'orange'
    }
  ];

  const typeValues = [
    {
      value: 'CLOTHING',
      name: 'Clothing'
    },
    {
      value: 'CHARM',
      name: 'Charm'
    },
    {
      value: 'VEHICLE',
      name: 'Vehicle'
    },
    {
      value: 'PET',
      name: 'Pet'
    }
  ];

  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [openDelete, setopenDelete] = useState();
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');

  const handleCreateMenu = () => setOpenAdd(!openAdd);
  const handleopenDelete = () => setopenDelete(!openDelete);
  const handleOpenEdit = () => setOpenEdit(!openEdit);
  const handleCloseSnackbar = () => {
    setError(null);
    setSuccessMessage('');
  };

  async function initData() {
    setLoading(true);
    setError(null);
    try {
      const res = await getItems();
      if (res?.status === 200 || res?.status === 201) {
        setItems(res?.data || []);
        setFilteredItems(res?.data || []);
      } else {
        setError('Failed to fetch items');
        setItems([]);
        setFilteredItems([]);
      }
    } catch (err) {
      setError('An error occurred while fetching items');
      setItems([]);
      setFilteredItems([]);
    } finally {
      setLoading(false);
    }
  }

  // Apply filters whenever filter values or items change
  useEffect(() => {
    let filtered = [...items];
    
    if (filterName) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }
    
    if (filterType) {
      filtered = filtered.filter(item => item.type === filterType);
    }
    
    if (filterRarity) {
      filtered = filtered.filter(item => item.rarity === filterRarity);
    }
    
    setFilteredItems(filtered);
  }, [items, filterName, filterType, filterRarity]);

  useEffect(() => {
    initData();
  }, []);

  function clearFormFields() {
    setName('');
    setRarity('');
    setImage('');
    setGems('');
    setCoins('');
    setType('');
    setUnrealItemId('');
    setTags('');
    setMetadata('');
    setItemId('');
    setIsActive(true);
  }

  async function sendData() {
    if (!name || !rarity || !type || !gems || !image || !unrealItemId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await createItem(
        name, 
        gems, 
        coins || 0, 
        type, 
        rarity, 
        image, 
        unrealItemId, 
        tags, 
        metadata, 
        itemId, 
        isActive
      );

      if (res.status === 200 || res.status === 201) {
        setSuccessMessage('Item created successfully!');
        clearFormFields();
        handleCreateMenu();
        await initData();
      } else if (res?.response?.status === 400) {
        setError(res?.response?.data?.message || 'Validation error');
      } else {
        setError('Failed to create item');
      }
    } catch (err) {
      setError('An error occurred while creating the item');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setLoading(true);
    try {
      const res = await deleteItem(deleteId);
      if (res.status === 200 || res.status === 204) {
        setSuccessMessage(`Item "${deleteName}" deleted successfully!`);
        handleopenDelete();
        setDeleteId(null);
        setDeleteName('');
        await initData();
      } else {
        setError('Failed to delete item');
      }
    } catch (err) {
      setError('An error occurred while deleting the item');
    } finally {
      setLoading(false);
    }
  }

  async function handleEdit() {
    if (!name || !rarity || !type || !gems || !unrealItemId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await editItem(
        editId, 
        name, 
        gems, 
        coins || 0, 
        type, 
        rarity, 
        unrealItemId, 
        tags, 
        metadata, 
        itemId, 
        isActive
      );
      if (res.status === 200 || res.status === 201) {
        setSuccessMessage('Item updated successfully!');
        clearFormFields();
        handleOpenEdit();
        setEditId(null);
        await initData();
      } else if (res?.response?.status === 400) {
        setError(res?.response?.data?.message || 'Validation error');
      } else {
        setError('Failed to update item');
      }
    } catch (err) {
      setError('An error occurred while updating the item');
    } finally {
      setLoading(false);
    }
  }

  function handleClearFilters() {
    setFilterName('');
    setFilterType('');
    setFilterRarity('');
  }

  function getRarityColor(rarity) {
    const rarityObj = selectValues.find(obj => obj.value === rarity);
    return rarityObj?.color || 'grey';
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={3}>
                  <MDBox>
                    <MDTypography variant='h4' fontWeight='medium'>
                      Items Management
                    </MDTypography>
                    <MDTypography variant='body2' color='text' mt={1}>
                      Manage all items in the catalog. Create, edit, and delete items with ease.
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    color='info'
                    onClick={() => {
                      clearFormFields();
                      handleCreateMenu();
                    }}
                    disabled={loading}
                  >
                    Add New Item
                  </MDButton>
                </MDBox>

                {/* Filters Section */}
                <MDBox mb={2}>
                  <MDTypography variant='h6' fontWeight='medium' mb={2}>
                    Filter Items
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Search by Name'
                        placeholder='Enter item name...'
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={filterType}
                          label='Type'
                          onChange={(e) => setFilterType(e.target.value)}
                        >
                          <MenuItem value=''>All Types</MenuItem>
                          {typeValues.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Rarity</InputLabel>
                        <Select
                          value={filterRarity}
                          label='Rarity'
                          onChange={(e) => setFilterRarity(e.target.value)}
                        >
                          <MenuItem value=''>All Rarities</MenuItem>
                          {selectValues.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <MDButton
                        color='secondary'
                        variant='outlined'
                        fullWidth
                        onClick={handleClearFilters}
                        disabled={loading}
                      >
                        Clear Filters
                      </MDButton>
                    </Grid>
                  </Grid>
                </MDBox>

                {/* Stats */}
                <MDBox display='flex' gap={2} mb={2}>
                  <Chip
                    label={`Total Items: ${items.length}`}
                    color='primary'
                    variant='outlined'
                  />
                  <Chip
                    label={`Filtered: ${filteredItems.length}`}
                    color='info'
                    variant='outlined'
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Items Display Section */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h6' fontWeight='medium' mb={2}>
                  Items Catalog
                </MDTypography>

                {loading ? (
                  <MDBox display='flex' justifyContent='center' alignItems='center' py={5}>
                    <CircularProgress />
                  </MDBox>
                ) : filteredItems && filteredItems.length > 0 ? (
                  <Grid container spacing={2}>
                    {filteredItems.map((item) => {
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                          <MDBox
                            sx={{
                              background: '#27314f',
                              borderRadius: '10px',
                              border: '0.5px solid #fff',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              height: '100%',
                              overflow: 'hidden',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
                              }
                            }}
                          >
                            <MDBox>
                              <MDTypography
                                sx={{
                                  padding: '10px',
                                  width: '100%',
                                  textAlign: 'center',
                                  background: '#121c35',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                  color: '#fff',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {item?.name}
                              </MDTypography>
                              <MDBox sx={{ padding: '10px', textAlign: 'center' }}>
                                <MDBox sx={{ mb: 1 }}>
                                  <Chip
                                    label={item?.rarity}
                                    size='small'
                                    sx={{
                                      backgroundColor: getRarityColor(item?.rarity),
                                      color: '#fff',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                  <Chip
                                    label={item?.type}
                                    size='small'
                                    variant='outlined'
                                    sx={{ ml: 1 }}
                                  />
                                  {!item?.isActive && (
                                    <Chip
                                      label='Inactive'
                                      size='small'
                                      color='error'
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </MDBox>
                                <MDBox sx={{ mt: 1 }}>
                                  <MDTypography variant='body2' color='text'>
                                    💎 {item?.priceGems} Gems
                                  </MDTypography>
                                  {item?.priceCoins > 0 && (
                                    <MDTypography variant='body2' color='text'>
                                      🪙 {item?.priceCoins} Coins
                                    </MDTypography>
                                  )}
                                </MDBox>
                                <MDTypography variant='caption' color='text' display='block'>
                                  Unreal ID: {item?.unrealItemId}
                                </MDTypography>
                                {item?.itemId && (
                                  <MDTypography variant='caption' color='text' display='block'>
                                    Item ID: {item?.itemId}
                                  </MDTypography>
                                )}
                                {item?.tags && item.tags.length > 0 && (
                                  <MDBox sx={{ mt: 1 }}>
                                    {item.tags.slice(0, 3).map((tag, idx) => (
                                      <Chip
                                        key={idx}
                                        label={tag}
                                        size='small'
                                        variant='outlined'
                                        sx={{ m: 0.25, fontSize: '10px' }}
                                      />
                                    ))}
                                  </MDBox>
                                )}
                              </MDBox>
                              {item?.file?.url && (
                                <MDBox
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '10px',
                                    minHeight: '150px'
                                  }}
                                >
                                  <img
                                    src={item?.file?.url}
                                    alt={item?.name}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '150px',
                                      objectFit: 'contain'
                                    }}
                                  />
                                </MDBox>
                              )}
                            </MDBox>
                            <MDBox sx={{ display: 'flex', padding: '10px', gap: '5px' }}>
                              <MDButton
                                size='small'
                                color='warning'
                                fullWidth
                                onClick={() => {
                                  handleOpenEdit();
                                  setEditId(item?.id);
                                  setName(item?.name);
                                  setRarity(item?.rarity);
                                  setImage(item?.file?.url);
                                  setGems(item?.priceGems);
                                  setCoins(item?.priceCoins);
                                  setType(item?.type);
                                  setUnrealItemId(item?.unrealItemId);
                                  setTags(item?.tags ? item.tags.join(', ') : '');
                                  setMetadata(item?.metadata ? JSON.stringify(item.metadata) : '');
                                  setItemId(item?.itemId || '');
                                  setIsActive(item?.isActive !== undefined ? item.isActive : true);
                                }}
                              >
                                Edit
                              </MDButton>
                              <MDButton
                                size='small'
                                color='error'
                                fullWidth
                                onClick={() => {
                                  handleopenDelete();
                                  setDeleteId(item?.id);
                                  setDeleteName(item?.name);
                                }}
                              >
                                Delete
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <MDBox
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    py={5}
                    sx={{ opacity: 0.5 }}
                  >
                    <MDTypography variant='body2' color='text'>
                      {items.length === 0 ? 'No items available. Create your first item!' : 'No items match the current filters.'}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={!!error || !!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={error ? 'error' : 'success'}
            variant='filled'
          >
            {error || successMessage}
          </Alert>
        </Snackbar>

        {/* Add Item Dialog */}
        <Dialog
          open={openAdd}
          onClose={loading ? null : handleCreateMenu}
          aria-labelledby='add-dialog-title'
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle id='add-dialog-title' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Add New Item
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px 30px' }}>
            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Item Name *</MDTypography>
              <MDInput
                fullWidth
                type='text'
                placeholder='Enter item name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Price (Gems) *</MDTypography>
              <MDInput
                fullWidth
                type='number'
                placeholder='Enter price in gems'
                value={gems}
                onChange={(e) => setGems(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Price (Coins)</MDTypography>
              <MDInput
                fullWidth
                type='number'
                placeholder='Enter price in coins'
                value={coins}
                onChange={(e) => setCoins(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Unreal Item ID *</MDTypography>
              <MDInput
                fullWidth
                type='number'
                placeholder='Enter Unreal Engine item ID'
                value={unrealItemId}
                onChange={(e) => setUnrealItemId(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Type *</MDTypography>
              <FormControl fullWidth variant='outlined'>
                <Select
                  id='type'
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  style={{ padding: '0.75rem 0' }}
                  disabled={loading}
                  displayEmpty
                >
                  <MenuItem value='' disabled>
                    Select item type
                  </MenuItem>
                  {typeValues?.map((e, i) => (
                    <MenuItem key={i} value={e?.value}>
                      {e?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Rarity *</MDTypography>
              <FormControl fullWidth variant='outlined'>
                <Select
                  id='rarity'
                  value={rarity}
                  onChange={(e) => setRarity(e.target.value)}
                  style={{ padding: '0.75rem 0' }}
                  disabled={loading}
                  displayEmpty
                >
                  <MenuItem value='' disabled>
                    Select rarity
                  </MenuItem>
                  {selectValues?.map((e, i) => (
                    <MenuItem key={i} value={e?.value}>
                      {e?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Item Image *</MDTypography>
              <StyledFileInput setImage={setImage} disabled={loading} />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Item ID (Unique)</MDTypography>
              <MDInput
                fullWidth
                type='text'
                placeholder='e.g., cool-hat-001'
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
                disabled={loading}
              />
              <MDTypography variant='caption' color='text' sx={{ mt: 0.5, display: 'block' }}>
                Optional: A unique identifier for this item
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Tags</MDTypography>
              <MDInput
                fullWidth
                type='text'
                placeholder='e.g., hat, clothing, fashion'
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                disabled={loading}
              />
              <MDTypography variant='caption' color='text' sx={{ mt: 0.5, display: 'block' }}>
                Comma-separated tags for categorization
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Metadata (JSON)</MDTypography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder='{"weight": 5, "durability": 100}'
                value={metadata}
                onChange={(e) => setMetadata(e.target.value)}
                disabled={loading}
                variant='outlined'
                size='small'
              />
              <MDTypography variant='caption' color='text' sx={{ mt: 0.5, display: 'block' }}>
                Optional: Additional data in JSON format
              </MDTypography>
            </MDBox>

            <MDBox>
              <FormControl fullWidth>
                <MDBox display='flex' alignItems='center' gap={2}>
                  <MDTypography sx={{ fontSize: '14px', fontWeight: '500' }}>Active Status</MDTypography>
                  <Select
                    value={isActive}
                    onChange={(e) => setIsActive(e.target.value)}
                    size='small'
                    disabled={loading}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </MDBox>
              </FormControl>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ padding: '20px' }}>
            <MDButton variant='text' onClick={handleCreateMenu} disabled={loading}>
              Cancel
            </MDButton>
            <MDButton
              color='info'
              onClick={sendData}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color='inherit' /> : 'Create Item'}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDelete}
          onClose={loading ? null : handleopenDelete}
          aria-labelledby='delete-dialog-title'
          maxWidth='xs'
          fullWidth
        >
          <DialogTitle id='delete-dialog-title' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Confirm Deletion
          </DialogTitle>
          <DialogContent>
            <MDTypography sx={{ textAlign: 'center', fontSize: '16px' }}>
              Are you sure you want to delete <strong>{deleteName}</strong>?
            </MDTypography>
            <MDTypography sx={{ textAlign: 'center', fontSize: '14px', color: 'text.secondary', mt: 2 }}>
              This action cannot be undone.
            </MDTypography>
          </DialogContent>
          <DialogActions sx={{ padding: '20px', justifyContent: 'center' }}>
            <MDButton
              variant='outlined'
              onClick={handleopenDelete}
              disabled={loading}
            >
              Cancel
            </MDButton>
            <MDButton
              color='error'
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color='inherit' /> : 'Delete'}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Edit Item Dialog */}
        <Dialog
          open={openEdit}
          onClose={loading ? null : handleOpenEdit}
          aria-labelledby='edit-dialog-title'
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle id='edit-dialog-title' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Edit Item
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px 30px' }}>
            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Item Name *</MDTypography>
              <MDInput
                fullWidth
                type='text'
                placeholder='Enter item name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Price (Gems) *</MDTypography>
              <MDInput
                fullWidth
                type='number'
                placeholder='Enter price in gems'
                value={gems}
                onChange={(e) => setGems(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Price (Coins)</MDTypography>
              <MDInput
                fullWidth
                type='number'
                placeholder='Enter price in coins'
                value={coins}
                onChange={(e) => setCoins(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Unreal Item ID *</MDTypography>
              <MDInput
                fullWidth
                type='number'
                placeholder='Enter Unreal Engine item ID'
                value={unrealItemId}
                onChange={(e) => setUnrealItemId(e.target.value)}
                disabled={loading}
              />
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Type *</MDTypography>
              <FormControl fullWidth variant='outlined'>
                <Select
                  id='type-edit'
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  style={{ padding: '0.75rem 0' }}
                  disabled={loading}
                >
                  {typeValues?.map((e, i) => (
                    <MenuItem key={i} value={e?.value}>
                      {e?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Rarity *</MDTypography>
              <FormControl fullWidth variant='outlined'>
                <Select
                  id='rarity-edit'
                  value={rarity}
                  onChange={(e) => setRarity(e.target.value)}
                  style={{ padding: '0.75rem 0' }}
                  disabled={loading}
                >
                  {selectValues?.map((e, i) => (
                    <MenuItem key={i} value={e?.value}>
                      {e?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Item Image</MDTypography>
              {image && (
                <MDBox sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={image}
                    alt='Current item'
                    style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                  />
                  <MDTypography variant='caption' color='text'>
                    Current image
                  </MDTypography>
                </MDBox>
              )}
              <StyledFileInput setImage={setImage} image={image} disabled={loading} />
              <MDTypography variant='caption' color='text' sx={{ mt: 1 }}>
                Note: Editing image requires backend support for file updates
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Item ID (Unique)</MDTypography>
              <MDInput
                fullWidth
                type='text'
                placeholder='e.g., cool-hat-001'
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
                disabled={loading}
              />
              <MDTypography variant='caption' color='text' sx={{ mt: 0.5, display: 'block' }}>
                Optional: A unique identifier for this item
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Tags</MDTypography>
              <MDInput
                fullWidth
                type='text'
                placeholder='e.g., hat, clothing, fashion'
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                disabled={loading}
              />
              <MDTypography variant='caption' color='text' sx={{ mt: 0.5, display: 'block' }}>
                Comma-separated tags for categorization
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography sx={{ fontSize: '14px', mb: 1, fontWeight: '500' }}>Metadata (JSON)</MDTypography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder='{"weight": 5, "durability": 100}'
                value={metadata}
                onChange={(e) => setMetadata(e.target.value)}
                disabled={loading}
                variant='outlined'
                size='small'
              />
              <MDTypography variant='caption' color='text' sx={{ mt: 0.5, display: 'block' }}>
                Optional: Additional data in JSON format
              </MDTypography>
            </MDBox>

            <MDBox>
              <FormControl fullWidth>
                <MDBox display='flex' alignItems='center' gap={2}>
                  <MDTypography sx={{ fontSize: '14px', fontWeight: '500' }}>Active Status</MDTypography>
                  <Select
                    value={isActive}
                    onChange={(e) => setIsActive(e.target.value)}
                    size='small'
                    disabled={loading}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </MDBox>
              </FormControl>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ padding: '20px' }}>
            <MDButton variant='text' onClick={handleOpenEdit} disabled={loading}>
              Cancel
            </MDButton>
            <MDButton
              color='info'
              onClick={handleEdit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color='inherit' /> : 'Update Item'}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}
